import { FC, useEffect, useMemo, useState } from 'react';
import { Menu } from 'antd';
import type { MenuProps } from 'antd';
import { MENU_ITEMS } from '@/devFrontData/menu';
import { manageMenuList } from '@/utils/menu/menu-utils';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@/contexts/themeContext';
import { Role } from '@/shared/types';
import { useAppSelector } from '@/hooks/reduxHooks';
import { collectParentKeys, filterMenuByRoles } from '@/utils';
import type { MenuFlag } from '@/shared/types';
import { useAgentClient, useAgentsPageAccess } from '@/features/agent-chat';

interface MenuListProps {
  onSelect?: () => void;
}
const findMenuKeyByPath = (path: string, items: any[]): string | null => {
  const parts = path.split('/').filter(Boolean);
  for (let i = parts.length; i > 0; i--) {
    const partialPath = `/${parts.slice(0, i).join('/')}`;
    for (const item of items) {
      if (item.link === partialPath) {
        return item.key;
      }
      if (item.children) {
        const childKey = findMenuKeyByPath(partialPath, item.children);
        if (childKey) return childKey;
      }
    }
  }
  return null;
};

const findParentKeys = (currentKey: string, items: any[]): string[] => {
  const parentKeys: string[] = [];

  const findParent = (items: any[], targetKey: string) => {
    for (const item of items) {
      if (item.children) {
        if (item.children.some((child: any) => child.key === targetKey)) {
          parentKeys.push(item.key);
        }
        findParent(item.children, targetKey);
      }
    }
  };

  findParent(items, currentKey);
  return parentKeys;
};
// Module-level so a missing profile gives the same array every render.
const EMPTY_ROLES: Role[] = [];

const MenuList: FC<MenuListProps> = () => {
  const { sidebarCollapseState, setSidebarCollapseState } = useTheme();
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const roles = useAppSelector(
    (s) => (s.auth.userData?.roles ?? EMPTY_ROLES) as Role[],
  );
  const { client } = useAgentClient();
  const { access } = useAgentsPageAccess(client);
  const agentsAllowed = access?.allowed === true;
  const flags = useMemo(
    () => new Set<MenuFlag>(agentsAllowed ? ['agents_page'] : []),
    [agentsAllowed],
  );
  const visibleMenu = useMemo(
    () => filterMenuByRoles(MENU_ITEMS, roles, flags),
    [roles, flags],
  );

  // Top-level keys drive the accordion below: opening one closes its siblings.
  const rootKeys = useMemo(
    () => visibleMenu.filter((item) => item.children).map((item) => item.key),
    [visibleMenu],
  );

  const updatedItems = useMemo(() => {
    const items = manageMenuList(visibleMenu);
    return sidebarCollapseState
      ? items?.map(({ children, ...item }: any) => item)
      : items;
  }, [visibleMenu, sidebarCollapseState]);

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    setSelectedKeys([e.key]);

    if (sidebarCollapseState) {
      const clickedItem = visibleMenu.find((m) => m.key === e.key);

      if (clickedItem?.children) {
        setSidebarCollapseState(false);
        setOpenKeys([e.key]);
      }
    }
  };

  useEffect(() => {
    const currentPath = location.pathname;
    const menuKey = findMenuKeyByPath(currentPath, visibleMenu);

    if (menuKey) {
      setSelectedKeys([menuKey]);

      const activeParentKeys = findParentKeys(menuKey, visibleMenu);

      if (!sidebarCollapseState) {
        setOpenKeys(activeParentKeys);
      }
    } else {
      setSelectedKeys([]);
    }
  }, [location.pathname, visibleMenu, sidebarCollapseState]);

  // Accordion: only one top-level section stays expanded. Keeping every section
  // open was what pushed the nav past the fold in the first place.
  const onOpenChange: MenuProps['onOpenChange'] = (keys) => {
    const nextKeys = Array.from(new Set(keys)) as string[];
    const latestOpenKey = nextKeys.find((key) => !openKeys.includes(key));

    if (!latestOpenKey) {
      // A section was closed rather than opened — keep the rest as-is.
      setOpenKeys(nextKeys);
      return;
    }

    // Nested submenus (if any are added later) stack normally inside their parent.
    if (!rootKeys.includes(latestOpenKey)) {
      setOpenKeys(nextKeys);
      return;
    }

    setOpenKeys([latestOpenKey]);
  };

  return (
    <Menu
      selectedKeys={selectedKeys}
      onClick={handleMenuClick}
      openKeys={openKeys}
      onOpenChange={onOpenChange}
      mode="inline"
      inlineCollapsed={sidebarCollapseState}
      style={{ background: 'none', border: 'none', width: '100%' }}
      items={updatedItems}
      theme="light"
    />
  );
};

export default MenuList;
